import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

import { setTransactionFilterOverride } from "redux/actions/userActions";
import CardsSkeleton from "./CardsSkeleton";

import MetricCard, { MetricBadge } from "./MetricCard";
import { CARD_TEXT, CARD_BORDER } from "./cardTokens";
import { STATUS_VIEW, TYPE_VIEW } from "./homeFilters";

const ALL = "All";

const sumCounts = (items) =>
  items.reduce(
    (acc, item) => ({
      atRiskCount: acc.atRiskCount + (item.atRiskCount || 0),
      onHoldCount: acc.onHoldCount + (item.onHoldCount || 0),
    }),
    { atRiskCount: 0, onHoldCount: 0 }
  );

// Renders just the collapsible title + 5 metric tiles for one category (Pre-transaction
// or Transaction). The toggle (By status/By type) and the Notice date/Transaction
// type/Transaction manager filters live once in DashboardHeader, shared by both
// sections, instead of being duplicated per section.
const DashboardSection = ({
  title,
  transactionCounts,
  isLoading,
  basePath,
  filterValues,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const view = filterValues.view || STATUS_VIEW;
  const sectionLabel = `${title.toLowerCase()}s`;

  if (!transactionCounts) return null;

  const { byStatus = [], byType = [] } = transactionCounts;
  const items = (view === STATUS_VIEW ? byStatus : byType).filter(
    (item) => item.transactionByName !== ALL
  );
  const totals = sumCounts(items);
  const columnCount = Math.min(items.length, 6) || 1;

  // Carries the section's own filter selections through to the Transaction page as
  // typeIds/statusIds/managerEmails/noticeDate - Transaction List's own filter shape,
  // which is unrelated to the transactionStatusIds/preTransactionStatusIds naming used
  // when the *shared* filter's selection is sent to the home-counts endpoint (see
  // homeFilters.js#buildHomeFilterRequest for that mapping).
  //
  // badgeType is only set when the click came from the At risk/On hold badge itself
  // (MetricBadge stops the click from bubbling up as a plain card click first) - it adds
  // Transaction List's own existing atRisk/onHold filter on top of the card's normal
  // status/type filter. Clicking anywhere else on the card keeps the existing behavior.
  const handleCardClick = (item, badgeType) => {
    const id = item.transactionById;
    const filterBy = view === STATUS_VIEW ? "Status" : "Type";

    const override = {
      ...(view === STATUS_VIEW ? { statusId: id } : { typeId: id }),
      ...(filterValues.noticeDate ? { noticeDate: filterValues.noticeDate } : {}),
      ...(filterValues.managerEmails.length ? { managerEmails: filterValues.managerEmails } : {}),
      ...(view === STATUS_VIEW && filterValues.typeIds.length
        ? { typeIds: filterValues.typeIds }
        : {}),
      ...(view === TYPE_VIEW && filterValues.statusIds.length
        ? { statusIds: filterValues.statusIds }
        : {}),
      ...(badgeType === "atRisk" ? { atRisk: true } : {}),
      ...(badgeType === "onHold" ? { onHold: true } : {}),
    };

    dispatch(setTransactionFilterOverride(override));
    history.push(`${basePath}?filterBy=${filterBy}&id=${id}`);
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#fff",
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "6px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        px: "24px",
        pt: "16px",
        pb: collapsed ? "16px" : "24px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Typography
            sx={{ fontSize: "20px", lineHeight: "28px", letterSpacing: "-0.1px", color: CARD_TEXT }}
          >
            {title}
          </Typography>
          {collapsed && (
            <>
              <MetricBadge type="atRisk" count={totals.atRiskCount} />
              <MetricBadge type="onHold" count={totals.onHoldCount} />
            </>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
          sx={{ width: 36, height: 36 }}
        >
          {collapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </IconButton>
      </Box>

      {!collapsed &&
        (isLoading ? (
          <CardsSkeleton />
        ) : (
          <Box
            sx={{
              width: "100%",
              minHeight: view === TYPE_VIEW ? "215px" : undefined,
              maxHeight: "215px",
              overflowY: "auto",
              pr: "4px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                columnGap: "24px",
                rowGap: "24px",
                width: "100%",
              }}
            >
              {items.map((item) => (
                <MetricCard
                  key={`${view}-${item.transactionById}`}
                  value={item.transactionByCount}
                  label={item.transactionByName}
                  atRiskCount={item.atRiskCount}
                  onHoldCount={item.onHoldCount}
                  sectionLabel={sectionLabel}
                  onClick={() => handleCardClick(item)}
                  onBadgeClick={(badgeType) => handleCardClick(item, badgeType)}
                />
              ))}
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default DashboardSection;
